import type { DocumentBlock, ResolvedDocument } from '@/types/blocks'
import type { DocumentType } from '@/types/BlockProperties'
import { secureFetch, secureJsonFetch } from '@/utils/SecureFetch'

/**
 * Query key factory for document-related queries.
 * Used with TanStack Query for cache management.
 */
export const documentKeys = {
	all: ['documents'] as const,
	details: () => [...documentKeys.all, 'detail'] as const,
	detail: (id: string) => [...documentKeys.details(), id] as const,
	resolved: (id: string) => [...documentKeys.detail(id), 'resolved'] as const,
}

async function handleResponse<T>(response: Response): Promise<T> {
	if (!response.ok) {
		if (response.status === 401) {
			throw new Error('UNAUTHORIZED')
		}
		const error = await response.text()
		throw new Error(error || `HTTP ${response.status}`)
	}
	return response.json()
}

/**
 * Fetch resolved document from V2 API with full block-based content.
 * Returns ResolvedDocument structure with children expanded.
 */
export async function fetchResolvedDocument(id: string): Promise<ResolvedDocument> {
	const response = await secureFetch(`/api/documents/${id}/resolved`)
	const data = await handleResponse<{ resolvedDocument: ResolvedDocument }>(response)
	return data.resolvedDocument
}

/**
 * Create a new document via V2 API.
 * Documents are block-based and belong to a playbook.
 *
 * Note: template_type uses DocumentType ('call_sheet' | 'wristband' | 'practice_script')
 * which is the actual document format, not DocumentTemplateType which is for UI templates.
 */
export interface CreateDocumentRequest {
	name: string
	template_type?: DocumentType
	description?: string
	sections?: Array<{ name: string; color?: string }>
}

export async function createDocument(
	playbookId: string,
	data: CreateDocumentRequest
): Promise<DocumentBlock> {
	const response = await secureJsonFetch(`/api/playbooks/${playbookId}/documents`, {
		method: 'POST',
		body: data,
	})
	const result = await handleResponse<{ document: DocumentBlock }>(response)
	return result.document
}
